/**
 * The admin panel's stylesheet.
 *
 * Injected once by admin-ui.ts and never removed, like the Settings screen's
 * sheet. Tokens come from src/styles/app.css, which index.html loads globally
 * and which never unmounts, so var(--brass) and friends are safe here.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export is one
 * template literal and a stray backtick terminates it. That has broken this
 * build twice before, both times with esbuild pointing ten lines away from the
 * actual cause.
 *
 * Deliberately plainer than the game panels. This is a tool, used by two or
 * three people, and every scrap of texture behind a data table costs
 * legibility. It keeps the palette and the typefaces so it is recognisably the
 * same product, and drops the tape, the grain and the lift.
 */
export default `
/* ── The screen ───────────────────────────────────────────────────────────
   A ROUTE, not an overlay. /admin replaces the page the way /play does, so
   there is no z-index to reason about here at all — the layer table in
   settings.css.ts (1001-1100 panels, 1250 settings, 1300 confirms) applies to
   things drawn OVER the game, and this is not one of them. The one exception
   is this screen's own confirm dialog, at the bottom of this file. */
.tg-adm {
  position: fixed; inset: 0;
  display: grid; grid-template-columns: 220px 1fr;
  background: var(--ground, #0c0a07);
  color: var(--bone, #ece2cd);
  font: 13px/1.5 var(--font-technical);
  overflow: hidden;
}

/* ── The rail ─────────────────────────────────────────────────────────── */
.tg-adm-rail {
  display: flex; flex-direction: column; gap: 2px;
  padding: 18px 12px;
  background: var(--panel-b, #1a1410);
  border-right: 1px solid var(--line, #3c301f);
  overflow-y: auto;
}
.tg-adm-brand {
  margin: 0 0 14px; padding: 0 6px;
  font: 700 20px/1 var(--font-display-stencil);
  letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--brass-bright, #dcb06a);
}
.tg-adm-group {
  margin: 14px 0 4px; padding: 0 6px;
  font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--bone-faint, #6c6047);
}
.tg-adm-tab {
  appearance: none; text-align: left; cursor: pointer;
  padding: 7px 10px; border: 1px solid transparent; border-radius: 3px;
  background: transparent; color: var(--bone-dim, #a89877);
  font: inherit;
}
.tg-adm-tab:hover { background: var(--panel-a, #2a2116); color: var(--bone, #ece2cd); }
.tg-adm-tab-on {
  background: var(--panel-a, #2a2116);
  border-color: var(--line, #3c301f);
  color: var(--brass-bright, #dcb06a);
}
.tg-adm-rail-spacer { flex: 1 1 auto; min-height: 18px; }
.tg-adm-back {
  display: block; padding: 8px 10px; border-radius: 3px;
  border: 1px solid var(--line, #3c301f);
  color: var(--bone-dim, #a89877); text-decoration: none; text-align: center;
}
.tg-adm-back:hover { color: var(--brass-hot, #ffbd5c); border-color: var(--brass, #ab8a53); }

/* ── The main column ──────────────────────────────────────────────────── */
.tg-adm-main { display: flex; flex-direction: column; overflow: hidden; }
.tg-adm-pane { flex: 1 1 auto; overflow-y: auto; padding: 22px 26px 40px; }

.tg-adm-head {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  padding-bottom: 12px; margin-bottom: 16px;
  border-bottom: 1px solid var(--line, #3c301f);
}
.tg-adm-title {
  margin: 0; font: 600 22px/1.2 var(--font-display, sans-serif);
  letter-spacing: 0.04em; color: var(--bone, #ece2cd);
}
.tg-adm-sub { margin: 4px 0 0; color: var(--bone-dim, #a89877); }
.tg-adm-toolbar { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
/* Toolbar controls are not inside a .tg-adm-field wrapper (they carry no
   label), so they need the input skin spelled out for them. */
.tg-adm-toolbar input, .tg-adm-toolbar select {
  padding: 6px 8px; border-radius: 3px;
  border: 1px solid var(--line, #3c301f);
  background: var(--well, #100c08); color: var(--bone, #ece2cd);
  font: inherit;
}
.tg-adm-toolbar input:focus, .tg-adm-toolbar select:focus {
  outline: none; border-color: var(--brass, #ab8a53);
}
.tg-adm-body > * + * { margin-top: 16px; }

/* ── The notice bar ───────────────────────────────────────────────────────
   Lives in the SHELL, above the pane, so a tab that rerenders its whole body
   after a write cannot wipe out the line explaining what the write did. */
.tg-adm-notice {
  padding: 9px 26px; border-bottom: 1px solid var(--line, #3c301f);
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
}
.tg-adm-notice:empty { display: none; }
.tg-adm-notice-ok  { background: rgba(116,133,74,0.18); color: var(--moss, #74854a); }
.tg-adm-notice-err { background: rgba(168,69,44,0.18); color: var(--rust, #a8452c); }
.tg-adm-notice button {
  appearance: none; background: none; border: 0; cursor: pointer;
  color: inherit; font: inherit; opacity: 0.7;
}

/* ── Controls ─────────────────────────────────────────────────────────── */
.tg-adm-btn {
  appearance: none; cursor: pointer;
  padding: 6px 12px; border-radius: 3px;
  border: 1px solid var(--line, #3c301f);
  background: var(--panel-a, #2a2116); color: var(--bone, #ece2cd);
  font: inherit;
}
.tg-adm-btn:hover:not(:disabled) { border-color: var(--brass, #ab8a53); color: var(--brass-hot, #ffbd5c); }
.tg-adm-btn:disabled { opacity: 0.45; cursor: default; }
.tg-adm-btn-primary {
  border-color: var(--brass, #ab8a53); color: var(--brass-bright, #dcb06a);
}
.tg-adm-btn-danger { border-color: var(--rust, #a8452c); color: var(--rust, #a8452c); }
.tg-adm-btn-danger:hover:not(:disabled) { background: rgba(168,69,44,0.15); color: #e0836a; }

.tg-adm-field { display: flex; flex-direction: column; gap: 4px; }
.tg-adm-label { font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--bone-dim, #a89877); }
.tg-adm-hint { font-size: 11px; color: var(--bone-faint, #6c6047); }
.tg-adm-field input,
.tg-adm-field textarea,
/* .tg-adm-field select, NOT .tg-adm select: the toolbar selects (the status
   filter on Players, the date range on Invoices) are NOT inside a field
   wrapper, and the 100% width below pushed each of them onto its own line,
   breaking the toolbar into three stacked rows. */
.tg-adm-field select {
  padding: 6px 8px; border-radius: 3px;
  border: 1px solid var(--line, #3c301f);
  background: var(--well, #100c08); color: var(--bone, #ece2cd);
  font: inherit; width: 100%; box-sizing: border-box;
}
.tg-adm-field textarea { resize: vertical; font-family: var(--font-technical); }
.tg-adm-field input:focus, .tg-adm-field textarea:focus, .tg-adm-field select:focus {
  outline: none; border-color: var(--brass, #ab8a53);
}
.tg-adm-check { display: flex; align-items: center; gap: 7px; color: var(--bone-dim, #a89877); }
.tg-adm-check input { accent-color: var(--brass, #ab8a53); }

.tg-adm-form {
  display: grid; gap: 12px;
  padding: 14px; border: 1px solid var(--line, #3c301f); border-radius: 4px;
  background: var(--panel-b, #1a1410);
}
.tg-adm-form-row { display: flex; gap: 10px; flex-wrap: wrap; align-items: flex-end; }
.tg-adm-form-row > .tg-adm-field { flex: 1 1 180px; }
/* Side-by-side FORMS rather than fields: flex-end would float the shorter one
   against the taller one's baseline, which reads as a rendering accident. */
.tg-adm-form-row-top { align-items: stretch; }
.tg-adm-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.tg-adm-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; }
.tg-adm-divider { border: none; border-top: 1px solid var(--line, #3c301f); margin: 8px 0; }

/* ── Tables ───────────────────────────────────────────────────────────── */
.tg-adm-tablewrap { overflow-x: auto; border: 1px solid var(--line, #3c301f); border-radius: 4px; }
.tg-adm-table { width: 100%; border-collapse: collapse; }
.tg-adm-table th, .tg-adm-table td {
  padding: 8px 10px; text-align: left; vertical-align: top;
  border-bottom: 1px solid rgba(60,48,31,0.6);
}
.tg-adm-table th {
  position: sticky; top: 0; z-index: 1;
  background: var(--panel-a, #2a2116);
  font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--bone-dim, #a89877); white-space: nowrap;
}
.tg-adm-table tr:last-child td { border-bottom: 0; }
.tg-adm-table tbody tr:hover { background: rgba(42,33,22,0.5); }
.tg-adm-tight { width: 1%; white-space: nowrap; }
/* NOT wrapping. The actions cell is tight (width 1%), so a wrapping flex row
   lets the browser satisfy that 1% by stacking the buttons — four actions
   became three lines and every row grew to 130px tall. nowrap makes the cell's
   min-content width the whole button row instead, which is what tight plus
   white-space: nowrap on the cell was always meant to produce.
   (No backticks anywhere in this file, comments included — see the header. A
   stray one here terminated the export and broke the whole bundle, which is
   the third time that has happened and the second time in this file.) */
.tg-adm-rowacts { display: flex; gap: 6px; flex-wrap: nowrap; }
/* Balances: one figure per line, the unit dimmed and lighter than the number,
   so a column of them is scanned as numbers with labels rather than as prose. */
.tg-adm-bal { display: block; white-space: nowrap; }
.tg-adm-bal em { font-style: normal; color: var(--bone-faint, #6c6047); }
.tg-adm-mono { font-family: var(--font-technical); color: var(--bone-dim, #a89877); }

.tg-adm-empty {
  padding: 22px; text-align: center; border-radius: 4px;
  border: 1px dashed var(--line, #3c301f); color: var(--bone-faint, #6c6047);
}
.tg-adm-pager { display: flex; align-items: center; gap: 12px; }
.tg-adm-pageno { color: var(--bone-dim, #a89877); }

.tg-adm-badge {
  display: inline-block; padding: 1px 7px; border-radius: 9px;
  font-size: 11px; letter-spacing: 0.04em;
  border: 1px solid currentColor;
}
.tg-adm-badge-ok   { color: var(--moss, #74854a); }
.tg-adm-badge-warn { color: var(--warn, #c98a2e); }
.tg-adm-badge-bad  { color: var(--rust, #a8452c); }
.tg-adm-badge-mute { color: var(--bone-faint, #6c6047); }

/* ── Dashboard tiles ──────────────────────────────────────────────────── */
.tg-adm-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; }
.tg-adm-stat {
  padding: 14px; border: 1px solid var(--line, #3c301f); border-radius: 4px;
  background: var(--panel-b, #1a1410);
}
.tg-adm-stat-n {
  display: block; font: 600 28px/1.1 var(--font-display, sans-serif);
  color: var(--brass-bright, #dcb06a);
}
.tg-adm-stat-l {
  display: block; margin-top: 4px;
  font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--bone-dim, #a89877);
}
.tg-adm-stat-attn .tg-adm-stat-n { color: var(--warn, #c98a2e); }

/* ── The detail drawer (a user, a withdrawal) ─────────────────────────── */
.tg-adm-drawer {
  padding: 14px; border: 1px solid var(--brass, #ab8a53); border-radius: 4px;
  background: var(--panel-b, #1a1410);
}
.tg-adm-kv { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 8px 18px; }
.tg-adm-kv div { display: flex; gap: 8px; justify-content: space-between; border-bottom: 1px dotted rgba(60,48,31,0.8); padding-bottom: 3px; }
.tg-adm-kv dt, .tg-adm-kv span:first-child { color: var(--bone-dim, #a89877); }
.tg-adm-kv span:last-child { text-align: right; word-break: break-word; }

/* ── The player type-ahead (invoice composer) ─────────────────────────────
   The results float over whatever is below rather than pushing the rest of the
   form down — a list that reflows the fields under the cursor moves the next
   control out from under the mouse between one keystroke and the next. */
.tg-adm-psearch { position: relative; }
.tg-adm-psearch-input {
  padding: 6px 8px; border-radius: 3px; width: 100%; box-sizing: border-box;
  border: 1px solid var(--line, #3c301f);
  background: var(--well, #100c08); color: var(--bone, #ece2cd);
  font: inherit;
}
.tg-adm-psearch-input:focus { outline: none; border-color: var(--brass, #ab8a53); }
.tg-adm-psearch-chip {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 5px 8px; border-radius: 3px;
  border: 1px solid var(--brass, #ab8a53); background: var(--well, #100c08);
}
.tg-adm-psearch-chip[hidden] { display: none; }
.tg-adm-psearch-name { color: var(--bone, #ece2cd); font-weight: 600; }
.tg-adm-psearch-results {
  position: absolute; left: 0; right: 0; top: 100%; z-index: 5;
  margin-top: 3px; max-height: 280px; overflow-y: auto;
  border: 1px solid var(--brass, #ab8a53); border-radius: 3px;
  background: var(--panel-a, #2a2116);
  box-shadow: 0 10px 26px rgba(0,0,0,0.55);
}
.tg-adm-psearch-results[hidden] { display: none; }
.tg-adm-psearch-msg { margin: 0; padding: 9px 10px; color: var(--bone-faint, #6c6047); }
.tg-adm-psearch-opt {
  display: block; width: 100%; text-align: left; cursor: pointer;
  padding: 7px 10px; border: 0; border-bottom: 1px solid rgba(60,48,31,0.6);
  background: transparent; color: var(--bone-dim, #a89877); font: inherit;
}
.tg-adm-psearch-opt:last-child { border-bottom: 0; }
.tg-adm-psearch-opt:hover, .tg-adm-psearch-on { background: var(--panel-b, #1a1410); }
.tg-adm-psearch-opt > span { display: block; }
.tg-adm-psearch-id { font-size: 11px; color: var(--bone-faint, #6c6047); }

/* ── A freshly minted API key ──────────────────────────────────────────── */
.tg-adm-secret {
  margin: 8px 0; padding: 10px;
  border: 1px dashed var(--brass, #ab8a53); border-radius: 3px;
  background: var(--well, #100c08); color: var(--terminal, #6fdc8f);
  word-break: break-all; user-select: all;
}

/* ── Collapsed CMS rows (FAQ, legal, social links) ────────────────────── */
.tg-adm-cmsrow {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 9px 12px; border: 1px solid var(--line, #3c301f); border-radius: 3px;
  background: var(--panel-b, #1a1410);
}
.tg-adm-cmsrow + .tg-adm-cmsrow { margin-top: 6px; }
.tg-adm-cmsrow-main { display: flex; align-items: center; gap: 10px; min-width: 0; }
.tg-adm-cmsrow-main > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ── Icon / share-image slots (Meta tags) ─────────────────────────────────
   The preview sits on a checkerboard so a transparent favicon is visibly
   transparent rather than looking like it has a dark background baked in. */
.tg-adm-icon {
  display: flex; align-items: center; gap: 14px;
  padding: 10px 0; border-bottom: 1px solid rgba(60,48,31,0.6);
}
.tg-adm-icon:last-of-type { border-bottom: 0; }
.tg-adm-icon-preview {
  flex: 0 0 auto; width: 56px; height: 56px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--line, #3c301f); border-radius: 3px;
  color: var(--bone-faint, #6c6047); font-size: 11px;
  background-color: #2b2b2b;
  background-image:
    linear-gradient(45deg, #3a3a3a 25%, transparent 25%, transparent 75%, #3a3a3a 75%),
    linear-gradient(45deg, #3a3a3a 25%, transparent 25%, transparent 75%, #3a3a3a 75%);
  background-size: 12px 12px;
  background-position: 0 0, 6px 6px;
}
.tg-adm-icon-preview-wide { width: 140px; }
.tg-adm-icon-preview img { max-width: 100%; max-height: 100%; }
.tg-adm-icon-meta { flex: 1 1 auto; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.tg-adm-icon-url { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* The file input is hidden INSIDE its label, so the label IS the button. */
.tg-adm-btn input[type="file"] { display: none; }
label.tg-adm-btn { display: inline-block; }

/* ── The confirm dialog ───────────────────────────────────────────────────
   1300, matching bootstrap.ts's confirmDialog, so the two look like the same
   thing wherever a destructive action is taken. The panel itself is a route
   and needs no layer, but this is drawn over it. */
.tg-adm-modal {
  position: fixed; inset: 0; z-index: 1300;
  display: flex; align-items: center; justify-content: center; padding: 24px;
  background: rgba(6,4,2,0.8);
}
.tg-adm-modal-card {
  width: min(460px, 100%); padding: 18px;
  border: 1px solid var(--brass, #ab8a53); border-radius: 4px;
  background: var(--panel-a, #2a2116);
}
.tg-adm-modal-card h3 {
  margin: 0 0 8px; font: 600 18px/1.2 var(--font-display, sans-serif);
  color: var(--bone, #ece2cd);
}
.tg-adm-modal-card p { margin: 0 0 16px; color: var(--bone-dim, #a89877); }
.tg-adm-modal-acts { display: flex; gap: 8px; justify-content: flex-end; }

/* ── Narrow screens ───────────────────────────────────────────────────────
   The panel is a desktop tool and is not being made responsive, but it should
   not become unusable on a laptop in a hurry: the rail collapses to a strip of
   tabs across the top rather than eating half the width. */
@media (max-width: 760px) {
  .tg-adm { grid-template-columns: 1fr; grid-template-rows: auto 1fr; }
  .tg-adm-rail { flex-direction: row; flex-wrap: wrap; overflow-x: auto; padding: 10px; }
  .tg-adm-brand, .tg-adm-group, .tg-adm-rail-spacer { display: none; }
  .tg-adm-pane { padding: 16px; }
}
`;
